import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { clearMessageHistory } from '../../../../utils/MessageHistory.js';
import { uploadFile } from '../../../../utils/socketManager.js';
import { clearStatusAnalyzeFile } from '../../../../store/slices/uploadAndAnalyzeAI.js';
import { validateFile } from '../../../../utils/Optimizer/fileValidation';
import ErrorSnackbar from '../../../common/ErrorSnackbar/ErrorSnackbar';
import { MAX_FILE_SIZE_MB } from '../../../../constants/fileSize';
import {
  OptimizerButton,
  OptimizerText,
  OptimizerContainer,
} from './Optimizer.styles';
const Optimizer = () => {
  const { t } = useTranslation(['optimizer', 'common']);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isDragActive, setIsDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validFile, setValidFile] = useState<File | null>(null);
  const handleFileUpload = () => {
    try {
      if (!validFile) {
        setError(t('common:fileUpload.noFileSelected'));
        return;
      }
      dispatch(clearStatusAnalyzeFile());
      uploadFile(validFile);
      localStorage.removeItem('priceSummary');
      localStorage.removeItem('selectedOptions');
      clearMessageHistory();
      navigate('/optimizer-list');
    }
    catch (error) {
      console.error(error);
      setError(t('common:fileUpload.uploadError'));
    }
  };
  useEffect(() => {
    if (validFile) {
      handleFileUpload();
    }
  }, [validFile]);
  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    noClick: false,
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
    onDropAccepted: () => setIsDragActive(false),
    onDropRejected: (rejectedFiles) => {
      setIsDragActive(false);
      const error = rejectedFiles[0]?.errors[0]?.message;
      setError(error || t('common:fileUpload.uploadError'));
      setValidFile(null);
    },
    onDrop: (acceptedFiles) => {
      setIsDragActive(false);
      const file = acceptedFiles[0];
      if (!file) {
        setError(t('common:fileUpload.noFileSelected'));
        setValidFile(null);
        return;
      }
      const validationResult = validateFile(file);
      if (!validationResult.isValid) {
        setError(validationResult.error || t('common:fileUpload.invalidFile'));
        setValidFile(null);
        return;
      }
      setError(null);
      setValidFile(file);
    },
  });
  return (
    <OptimizerContainer optVariant="main">
      <OptimizerText optVariant="title">
        {t('optimizer:title')}
      </OptimizerText>
      <OptimizerText optVariant="description">
        {t('optimizer:description')}
      </OptimizerText>
      <OptimizerContainer
        optVariant="uploadWrapper"
        {...getRootProps()}
        className={isDragActive ? 'drag-active' : ''}
      >
        <OptimizerContainer optVariant="uploadBlock">
          <input {...getInputProps()} />
          <img src="/icons/upload.svg" alt="file-upload" width="50" height="50" />
          <OptimizerText optVariant="upload">
            {t('optimizer:upload.dragDrop')}
          </OptimizerText>
          <OptimizerText optVariant="upload">
            {t('optimizer:upload.or')}
          </OptimizerText>
          <OptimizerButton optVariant="contained"
            endIcon={<FileUploadIcon />}>
            {t('optimizer:upload.selectFile')}
          </OptimizerButton>
          <OptimizerContainer optVariant="validation">
            <OptimizerText optVariant="sub">
              {t('optimizer:upload.supportedFormats')}
            </OptimizerText>
            <OptimizerText optVariant="sub">
              {t('optimizer:upload.maxFileSize', { size: MAX_FILE_SIZE_MB })}
            </OptimizerText>
          </OptimizerContainer>
        </OptimizerContainer>
      </OptimizerContainer>
      <ErrorSnackbar error={error || ''} onClose={() => setError(null)} />
    </OptimizerContainer>
  );
};
export default Optimizer;
